import { DateTime } from "luxon";
import {
  ACTION_ACCEPT,
  ACTION_APPROVE,
  ACTION_CANCEL,
  ACTION_CANCEL_ACCEPT,
  ACTION_CANCEL_APPROVE,
  ACTION_CANCEL_DECLINE,
  ACTION_CANCEL_OFFER,
  ACTION_CANCEL_REFUSE,
  ACTION_DECLINE,
  ACTION_REFUSE,
  ACTION_REINSTATE,
  ACTION_RETRACT,
  FREEFORM_REASON_CHAR_LIMIT,
  GIVEAWAY,
  IAM_CONTRACT,
  MAX_IAM_SHIFT_LENGTH_MINUTES,
  MAX_NON_CONTRACT_SHIFT_LENGTH_MINUTES,
  MAX_TWU_SHIFT_LENGTH_MINUTES,
  MIN_IAM_SHIFT_LENGTH_MINUTES,
  MIN_NON_CONTRACT_SHIFT_LENGTH_MINUTES,
  NON_CONTRACT,
  TWU_CONTRACT,
  VALID_SWA_STATIONS,
} from "./constants";
import {
  getTimeDifferenceInMinutes,
  THREE_MONTHS_FROM,
  THREE_MONTHS_IN_FUTURE,
  THREE_YEARS_IN_PAST,
  TWO_MONTHS_IN_PAST,
} from "./dateHelper";
import {
  END_DATE_IS_REQUIRED,
  MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE,
  MIN_SHIFT_30_MINUTES_WARNING_MESSAGE,
  SHIFT_TIME_IS_REQUIRED,
  START_DATE_CANNOT_BE_IN_THE_PAST,
  START_DATE_IS_REQUIRED,
  TRADE_TYPE_IS_REQUIRED_MESSAGE,
  INVALID_EMPLOYEE_ID,
  CHAR_LIMIT_MESSAGE,
  EMPLOYEE_ID_REQUIRED,
  COMMENTS_REQUIRED,
} from "./messageConstants";
import { getTimeZoneFromSwaStation } from "./timeZoneService";
import {
  getEarliestShift,
  isExpiredAdminEventCheckRequired,
  isExpiredEmployeeEventCheckRequired,
  isHourSubmissionDeadlineRequired,
  isTwoMonthSubmissionDeadlineRequired,
  oneHourFromEarliestShift,
} from "./tradeFormEventHelper";
import {
  getEligibleWorkgroupsForGiveawayTradesGivenJobCode,
  getEligibleWorkgroupsGivenJobCode,
  getWorkgroupGivenJobCode,
  isEligibleForAccessOverride,
} from "./workgroupMapper";
import { getJobCodeData } from "./employeeConfigUtils";

// Validations for Employee Request Trade Page - START
export function validateStation(input) {
  if (input.trim() === "") {
    return "Station is required";
  }
  return "";
}

export function validateTradeFormType(input) {
  if (input.trim() === "") {
    return TRADE_TYPE_IS_REQUIRED_MESSAGE;
  }
  return "";
}

export function validateShiftWorkgroup(input) {
  if (input.trim() === "") {
    return "Shift Workgroup is required";
  }
  return "";
}

export function validateShiftStartDate(tradeType, input, otherShiftStartDate, station, isSubmission) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  var today = DateTime.now()
    .setZone(getTimeZoneFromSwaStation(station))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (input.toMillis() < today.toMillis()) {
    return START_DATE_CANNOT_BE_IN_THE_PAST;
  }
  if (input.toMillis() > THREE_MONTHS_IN_FUTURE(station).toMillis()) {
    return "Shift Date cannot be over 3 months in the future";
  }

  if (tradeType === "Time Trade" && isSubmission) {
    if (otherShiftStartDate) {
      if (
        input.year !== otherShiftStartDate.year ||
        input.month !== otherShiftStartDate.month ||
        input.day !== otherShiftStartDate.day
      ) {
        return "Start Dates must match on Time Trade";
      }
    }
  }

  return "";
}

export function validateShiftStartDateForAdminCreateTrade(
  tradeType,
  input,
  otherShiftStartDate,
  station,
  isSubmission
) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  if (input.toMillis() < TWO_MONTHS_IN_PAST(station).toMillis()) {
    return "Start Date past trade submission deadline";
  }
  if (input.toMillis() > THREE_MONTHS_IN_FUTURE(station).toMillis()) {
    return "Shift Date cannot be over 3 months in the future";
  }

  if (tradeType === "Time Trade" && isSubmission) {
    if (otherShiftStartDate) {
      if (
        input.year !== otherShiftStartDate.year ||
        input.month !== otherShiftStartDate.month ||
        input.day !== otherShiftStartDate.day
      ) {
        return "Start Dates must match on Time Trade";
      }
    }
  }

  return "";
}
export function validateShiftStartTimePeriod(station, startDate, hourInput, minInput) {
  let today = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  let startDateVal = startDate.set({ hour: hourInput, minute: minInput, second: 0 });
  if (startDateVal.year === today.year && startDateVal.month === today.month && startDateVal.day === today.day) {
    if (startDateVal.toMillis() < today.toMillis()) {
      return "Shift Time cannot be in the past";
    }
  }
  return "";
}

export function validateShiftStartTime(startDate, hourInput, minInput, station, tradeStartEvent, tradeType) {
  // It's expected that both hourInput and minInput have been touched when
  // this validation is called.
  if (hourInput.toString().trim() === "" || minInput.toString().trim() === "") {
    return SHIFT_TIME_IS_REQUIRED;
  }
  if (startDate === null) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }
  const validateShiftTimeErrorMsg = validateShiftStartTimePeriod(station, startDate, hourInput, minInput);
  if (validateShiftTimeErrorMsg !== "") {
    return validateShiftTimeErrorMsg;
  }

  const deadlineErrors = validateShiftOneHourDeadline(tradeStartEvent, station, tradeType);
  if (deadlineErrors !== "") {
    return deadlineErrors;
  }
  return "";
}

export function validateShiftOneHourDeadline(tradeStartEvent, station, tradeType) {
  let today = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  if (tradeStartEvent.offeredShiftStart.date === null) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }
  let offeredShiftStartDate = tradeStartEvent.offeredShiftStart.date.set({
    hour: tradeStartEvent.offeredShiftStart.hour,
    minute: tradeStartEvent.offeredShiftStart.minute,
    second: 0,
  });
  let requestedShiftStartDate = null;

  if (
    tradeType !== "Giveaway" &&
    tradeStartEvent.requestedShiftStart.date !== null &&
    tradeStartEvent.requestedShiftStart.hour !== "" &&
    tradeStartEvent.requestedShiftStart.minute !== ""
  ) {
    requestedShiftStartDate = tradeStartEvent.requestedShiftStart.date.set({
      hour: tradeStartEvent.requestedShiftStart.hour,
      minute: tradeStartEvent.requestedShiftStart.minute,
      second: 0,
    });
  }

  const newTradeEvent = { offeredShiftStart: offeredShiftStartDate, requestedShiftStart: requestedShiftStartDate };
  let oneHourFrom = oneHourFromEarliestShift(newTradeEvent);
  if (today > oneHourFrom) {
    return "Shift time past 1-hour deadline";
  }
  return "";
}

export function validateShiftStartTimeForAdminCreateTrade(startDate, hourInput, minInput) {
  // It's expected that both hourInput and minInput have been touched when
  // this validation is called.
  if (hourInput.toString().trim() === "" || minInput.toString().trim() === "") {
    return SHIFT_TIME_IS_REQUIRED;
  }

  return "";
}

export function validateShiftEndDate(startDate, input, station) {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }
  if (startDate === null) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }

  var today = DateTime.now()
    .setZone(getTimeZoneFromSwaStation(station))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (input.toMillis() < today.toMillis()) {
    return "End Date cannot be in the past";
  }
  if (input.toMillis() < startDate.toMillis()) {
    return "Invalid End Date";
  }

  return "";
}

export function validateShiftEndDateForAdminCreateTrade(startDate, input, station) {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }

  if (startDate !== null) {
    if (input.toMillis() < startDate.toMillis()) {
      return "Invalid End Date";
    }
  }

  return "";
}

export const setStartDateTime = (startDate, startHourInput, startMinInput, station) => {
  return startDate.set({ hour: startHourInput, minute: startMinInput, second: 0, millisecond: 0 });
};

export const setEndDateTime = (endDate, endHourInput, endMinInput, station) => {
  return endDate.set({ hour: endHourInput, minute: endMinInput, second: 0, millisecond: 0 });
};

export function validateShiftEndTime(
  startDate,
  endDate,
  startHourInput,
  startMinInput,
  endHourInput,
  endMinInput,
  unionContract,
  station
) {
  // It's expected that both hourInputs and both minInputs have been touched when
  // this validation is called.
  if (endHourInput.toString().trim() === "" || endMinInput.toString().trim() === "") {
    return SHIFT_TIME_IS_REQUIRED;
  }
  if (
    startHourInput.toString().trim() === "" ||
    startMinInput.toString().trim() === "" ||
    startDate === null ||
    endDate === null
  ) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }

  var startDateVal = setStartDateTime(startDate, startHourInput, startMinInput, station);
  var endDateVal = setEndDateTime(endDate, endHourInput, endMinInput, station);

  if (endDateVal.toMillis() <= startDateVal.toMillis()) {
    return "Invalid End Time";
  }

  let timeDifference = getTimeDifferenceInMinutes(startDateVal, endDateVal);

  if (unionContract === TWU_CONTRACT) {
    if (timeDifference > MAX_TWU_SHIFT_LENGTH_MINUTES) {
      return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    }
  } else if (unionContract === NON_CONTRACT) {
    if (timeDifference > MAX_NON_CONTRACT_SHIFT_LENGTH_MINUTES) {
      return MAX_8_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    } else if (timeDifference < MIN_NON_CONTRACT_SHIFT_LENGTH_MINUTES) {
      return MIN_SHIFT_30_MINUTES_WARNING_MESSAGE;
    }
  } else if (unionContract === IAM_CONTRACT) {
    if (timeDifference > MAX_IAM_SHIFT_LENGTH_MINUTES) {
      return MAX_SHIFT_10_AND_ONE_HALF_HOURS_WARNING_MESSAGE;
    } else if (timeDifference < MIN_IAM_SHIFT_LENGTH_MINUTES) {
      return MIN_SHIFT_30_MINUTES_WARNING_MESSAGE;
    }
  }

  return "";
}

export function validateDuplicateShifts(
  offeredShiftStart,
  offeredShiftStartHour,
  offeredShiftStartMinute,
  offeredShiftEnd,
  offeredShiftEndHour,
  offeredShiftEndMinute,
  requestedShiftStart,
  requestedShiftStartHour,
  requestedShiftStartMinute,
  requestedShiftEnd,
  requestedShiftEndHour,
  requestedShiftEndMinute,
  isAdminPage
) {
  if (
    offeredShiftStart !== null &&
    offeredShiftStartHour !== "" &&
    offeredShiftStartMinute !== "" &&
    offeredShiftEnd !== null &&
    offeredShiftEndHour !== "" &&
    offeredShiftEndMinute !== "" &&
    requestedShiftStart !== null &&
    requestedShiftStartHour !== "" &&
    requestedShiftStartMinute !== "" &&
    requestedShiftEnd !== null &&
    requestedShiftEndHour !== "" &&
    requestedShiftEndMinute !== ""
  ) {
    if (
      offeredShiftStart.day === requestedShiftStart.day &&
      offeredShiftStart.month === requestedShiftStart.month &&
      offeredShiftStart.year === requestedShiftStart.year &&
      offeredShiftEnd.day === requestedShiftEnd.day &&
      offeredShiftEnd.month === requestedShiftEnd.month &&
      offeredShiftEnd.year === requestedShiftEnd.year &&
      offeredShiftStartHour === requestedShiftStartHour &&
      offeredShiftStartMinute === requestedShiftStartMinute &&
      offeredShiftEndHour === requestedShiftEndHour &&
      offeredShiftEndMinute === requestedShiftEndMinute
    ) {
      if (isAdminPage) {
        return "Employee #2 Shift cannot be the same as Employee #1";
      } else {
        return "Shift Requested cannot be the same as Shift Offered";
      }
    }
  }
  return "";
}

export function validateReliefShift(input) {
  if (input === true || input === false) {
    return "";
  }
  return "Relief Shift is required";
}

export function validateEmployee(employeeId, employeeFirstName, employeeLastName, employeeStation, employeeWorkgroup) {
  if (
    employeeId.trim() === "" ||
    employeeFirstName.trim() === "" ||
    employeeLastName.trim() === "" ||
    employeeWorkgroup.trim() === ""
  ) {
    return "Employee is required";
  }
  return "";
}

// Validations for Employee Request Trade Page - END

export function validateVerbalCheck(input, isRequired) {
  if (isRequired && !input) {
    return "Must be confirmed";
  }
  return "";
}

export function validateCannedReason(input, isRequired) {
  const trimmedInput = input.trim();
  if (isRequired && trimmedInput === "") {
    return "Reason is required";
  }
  return "";
}

export function validateReasonComments(input, isRequired) {
  const trimmedInput = input.trim();
  if (isRequired && trimmedInput === "") {
    return COMMENTS_REQUIRED;
  }
  if (trimmedInput.length > FREEFORM_REASON_CHAR_LIMIT) {
    return CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT);
  }
  return "";
}

export function validateEmployeeId(input) {
  if (input.trim() === "") {
    return EMPLOYEE_ID_REQUIRED;
  } else if (input.trim().charAt(0).toUpperCase() !== "E") {
    return INVALID_EMPLOYEE_ID + ' - start with "e"';
  } else if (!input.match("e\\d{0,6}$")) {
    return INVALID_EMPLOYEE_ID;
  }
  return "";
}

export function validateOwnTradeEmployeeId(input, userId) {
  if (userId === input) {
    return "You cannot create a trade for yourself";
  }
  return "";
}

export function validateEmployeeDataForManageAccess(input, superuserStation) {
  if (Object.keys(getJobCodeData()).indexOf(input.jobCode) < 0) {
    return INVALID_EMPLOYEE_ID;
  }
  if (!isEligibleForAccessOverride(input.jobCode)) {
    return "Not eligible for Admin access";
  }
  if (input.activeStation !== superuserStation) {
    return "Current Employee location is not " + superuserStation;
  }
  return "";
}

export function validateEmployeeOneDataForAdminCreateTrade(input, selectedStation, employeeTwoData, shiftWorkgroup) {
  if (input === null) {
    return "Not loaded";
  }

  if (Object.keys(getJobCodeData()).indexOf(input.jobCode) < 0) {
    return INVALID_EMPLOYEE_ID;
  }
  if (input.activeStation !== selectedStation && input.homeStation !== selectedStation) {
    return "Employee must work in " + selectedStation;
  }
  if (getWorkgroupGivenJobCode(input.jobCode) !== shiftWorkgroup) {
    return "Employee must be in selected Shift Workgroup";
  }
  if (employeeTwoData !== null) {
    if (input.eid === employeeTwoData.eid) {
      return "Employee #2 is the same as Employee #1. Please select another Employee.";
    }
  }

  return "";
}

export function validateEmployeeTwoDataForAdminCreateTrade(
  input,
  selectedStation,
  employeeOneData,
  shiftWorkgroup,
  tradeFormType
) {
  if (input === null) {
    return "Not loaded";
  }

  if (Object.keys(getJobCodeData()).indexOf(input.jobCode) < 0) {
    return INVALID_EMPLOYEE_ID;
  }
  if (input.activeStation !== selectedStation && input.homeStation !== selectedStation) {
    return "Employee must work in " + selectedStation;
  }

  if (employeeOneData !== null) {
    if (tradeFormType === GIVEAWAY) {
      if (
        !getEligibleWorkgroupsForGiveawayTradesGivenJobCode(employeeOneData.jobCode).includes(
          getWorkgroupGivenJobCode(input.jobCode)
        )
      ) {
        return "Employees ineligible to trade";
      }
    } else {
      if (
        !getEligibleWorkgroupsGivenJobCode(employeeOneData.jobCode).includes(getWorkgroupGivenJobCode(input.jobCode))
      ) {
        return "Employees ineligible to trade";
      }
    }
    if (input.eid === employeeOneData.eid) {
      return "Employee #2 is the same as Employee #1. Please select another Employee.";
    }
  }

  return "";
}

export function validateAccessLevel(input) {
  if (input.trim() === "") {
    return "Access Level is required";
  }
  return "";
}

export function validateAddAdminStationForManageAccess(input, currentStations) {
  if (input.trim() === "") {
    return "Admin Station is required";
  }
  if (currentStations.length >= 4) {
    return "Cannot add more than 4 stations";
  }
  if (currentStations.indexOf(input) > -1) {
    return "Station has already been added: " + input;
  }
  if (!VALID_SWA_STATIONS().includes(input)) {
    return "Invalid Station";
  }
  return "";
}

export function validateSubmitAdminStationsForManageAccess(input) {
  if (input.length < 2) {
    return "At least 2 stations must be added";
  } else if (input.length > 4) {
    return "Cannot add more than 4 stations";
  }
  return "";
}

export function validateAdminComments(input) {
  const trimmedInput = input.trim();
  if (trimmedInput === "") {
    return "Comments are required";
  }
  if (trimmedInput.length > FREEFORM_REASON_CHAR_LIMIT) {
    return CHAR_LIMIT_MESSAGE(FREEFORM_REASON_CHAR_LIMIT);
  }
  return "";
}

export function validateStartDateFilter(input, endDateFilter, station) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  if (endDateFilter.toMillis() < input.toMillis()) {
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() < THREE_YEARS_IN_PAST(station).toMillis()) {
    return "Start Date cannot be prior to 3 years in the past";
  }
  if (endDateFilter.toMillis() > THREE_MONTHS_FROM(station, input).toMillis()) {
    return "Start Date cannot be over 3 months before End Date";
  }

  return "";
}

export function validateEndDateFilter(input, startDateFilter, station) {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }

  if (input.toMillis() < startDateFilter.toMillis()) {
    // end date is before start date
    return "End Date cannot be prior to Start Date";
  }
  if (input.toMillis() > THREE_MONTHS_FROM(station, startDateFilter).toMillis()) {
    return "End Date cannot be over 3 months after Start Date";
  }
  return "";
}

export function validateSubmissionDeadline(tradeEvent, actionSelected) {
  let validationErrors = "";
  if (isTwoMonthSubmissionDeadlineRequired(actionSelected)) {
    validationErrors = validateIfEarliestShiftIsPastTwoMonths(tradeEvent, actionSelected);
  }

  if (isHourSubmissionDeadlineRequired(actionSelected)) {
    validationErrors = validateIfEarliestShiftIsWithinOneHour(tradeEvent, actionSelected);
  }

  if (isExpiredEmployeeEventCheckRequired(actionSelected)) {
    // This covers the small window where the client has not received the expired event yet
    validationErrors = validateIfEarliestShiftIsWithinOneHour(tradeEvent, actionSelected);
  }

  if (isExpiredAdminEventCheckRequired(actionSelected)) {
    // This covers the small window where the client has not received the expired event yet
    validationErrors = validateIfEarliestShiftIsStarted(tradeEvent, actionSelected);
  }

  return validationErrors;
}

export function validateIfEarliestShiftIsPastTwoMonths(event, action) {
  let earliestShift = getEarliestShift(event);
  let twoMonthsInPastVar = TWO_MONTHS_IN_PAST(event.station);
  if (earliestShift.toMillis() < twoMonthsInPastVar.toMillis()) {
    if (action === ACTION_CANCEL) {
      return "You cannot cancel a trade with shift dates over 2 months in the past!";
    }
    if (action === ACTION_REINSTATE) {
      return "You cannot reinstate a trade with shift dates over 2 months in the past!";
    }
  }
  return "";
}

export function validateIfEarliestShiftIsWithinOneHour(event, action) {
  let now = DateTime.now().toMillis();
  let oneHourFrom = oneHourFromEarliestShift(event);
  if (now > oneHourFrom) {
    switch (action) {
      case ACTION_CANCEL_OFFER:
        return "You cannot cancel a trade past the deadline: 1 hour before 1st shift in trade request!";
      case ACTION_ACCEPT:
        return "You cannot submit a trade past the deadline: 1 hour before 1st shift in trade request!";
      case ACTION_DECLINE:
        return "You cannot decline a trade past the deadline: 1 hour before 1st shift in trade request!";
      case ACTION_RETRACT:
        return "You cannot retract a trade past the deadline: 1 hour before 1st shift in trade request!";
      case ACTION_CANCEL_ACCEPT:
        return "You cannot submit a trade cancellation past the deadline: 1 hour before 1st shift in trade request!";
      case ACTION_CANCEL_DECLINE:
        return "You cannot decline a trade cancellation past the deadline: 1 hour before 1st shift in trade request!";
      default:
        break;
    }
  }
  return "";
}

export function validateIfEarliestShiftIsStarted(event, action) {
  let now = DateTime.now().toMillis();
  let earliestShift = getEarliestShift(event);
  if (now > earliestShift.toMillis()) {
    switch (action) {
      case ACTION_APPROVE:
        return "You cannot approve a trade past the deadline: start time of 1st shift in trade request!";
      case ACTION_REFUSE:
        return "You cannot refuse a trade past the deadline: start time of 1st shift in trade request!";
      case ACTION_CANCEL_APPROVE:
        return "You cannot approve a trade cancellation past the deadline: start time of 1st shift in trade request!";
      case ACTION_CANCEL_REFUSE:
        return "You cannot refuse a trade cancellation past the deadline: start time of 1st shift in trade request!";
      default:
        break;
    }
  }
  return "";
}

export const validatePhoneNumber = (phoneNumber, requiredMessage, invalidMessage) => {
  const isValidNumber = RegExp(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4}$/).test(phoneNumber);
  if (phoneNumber === "") {
    return requiredMessage;
  }
  if (!isValidNumber) {
    return invalidMessage;
  }

  return "";
};

export const validatePreference = (input) => {
  if (input.trim() === "") {
    return "Preference is required";
  }
  return "";
};
